/**
 * formMeta — shared vocabulary for the WCR-parity form builder (Phase C).
 *
 * One place for the question-type catalog (label + description + icon for the
 * type-picker grid and the collapsed cards), the operator menus for
 * branching/eligibility, and the calculation presets. Mirrors the WCR admin's
 * builder metadata (QuestionTypeSelector / ConditionBuilder /
 * EligibilityRuleBuilder / CalculationEditor) so the two builders stay in
 * lock-step, while the backend whitelist in crmForms.validateDefinition stays
 * authoritative for what actually validates.
 */
import type { WheelEvent } from 'react';
import {
  Type, AlignLeft, Mail, Phone, Hash, Ruler, Calendar,
  CircleDot, CheckSquare, ToggleLeft, Sliders, Calculator, LucideIcon,
} from 'lucide-react';

/**
 * Spread on every <input type="number"> in the builder. Two browser defaults
 * eat decimal thresholds (an A1c review flag at 6.5 is the canonical case):
 *   - step defaults to 1, which makes 6.5 a stepMismatch — the browser paints
 *     the field invalid and every spinner step snaps to a whole number;
 *   - a FOCUSED number input steps on mouse wheel in Chrome/Firefox, so
 *     scrolling the tall question modal down to "Update Question" right after
 *     typing walks 6.5 → 6 → 5 → 4 → 3 without the author seeing it.
 * Blurring on wheel lets the scroll through and leaves the value alone.
 * FormRenderer keeps its own copy — it is deliberately dependency-free.
 */
export const numberInputProps = {
  step: 'any',
  onWheel: (e: WheelEvent<HTMLInputElement>) => {
    if (document.activeElement === e.currentTarget) e.currentTarget.blur();
  },
};

export interface QuestionTypeMeta {
  value: string;
  label: string;
  description: string;
  icon: LucideIcon;
}

/** The 12 WCR-derived question types, in the order the picker grid shows them. */
export const QUESTION_TYPE_META: QuestionTypeMeta[] = [
  { value: 'text', label: 'Short Text', description: 'Single line text input', icon: Type },
  { value: 'textarea', label: 'Long Text', description: 'Multi-line text area', icon: AlignLeft },
  { value: 'email', label: 'Email', description: 'Email address with validation', icon: Mail },
  { value: 'phone', label: 'Phone', description: 'Phone number with formatting', icon: Phone },
  { value: 'number', label: 'Number', description: 'Numeric input with min/max', icon: Hash },
  { value: 'height', label: 'Height', description: 'Height in feet and inches', icon: Ruler },
  { value: 'date', label: 'Date', description: 'Date picker', icon: Calendar },
  { value: 'single_choice', label: 'Single Choice', description: 'Select one option', icon: CircleDot },
  { value: 'multiple_choice', label: 'Multiple Choice', description: 'Select multiple options', icon: CheckSquare },
  { value: 'yes_no', label: 'Yes / No', description: 'Simple yes or no question', icon: ToggleLeft },
  { value: 'scale', label: 'Scale', description: 'Numeric rating scale', icon: Sliders },
  { value: 'calculation', label: 'Calculation', description: 'Computed value (age, BMI)', icon: Calculator },
];

const TYPE_LABEL = new Map(QUESTION_TYPE_META.map((t) => [t.value, t.label]));
export const typeLabel = (t: string): string => TYPE_LABEL.get(t) || t;
export const typeIcon = (t: string): LucideIcon | null =>
  QUESTION_TYPE_META.find((x) => x.value === t)?.icon || null;

/** Human labels for both operator menus (branching uses a subset). */
export const OPERATOR_LABELS: Record<string, string> = {
  equals: 'equals',
  not_equals: 'does not equal',
  contains: 'contains',
  greater_than: 'is greater than',
  less_than: 'is less than',
  in: 'is one of',
  not_in: 'is none of',
  between: 'is between',
  not_between: 'is not between',
};

/** The branching operators that take a LIST of values rather than one. */
export const SHOW_IF_LIST_OPS = ['in', 'not_in'];
export const isListOperator = (op: string | undefined) => SHOW_IF_LIST_OPS.includes(op || '');

/** show_if operators offered for a given source-question type. `in`/`not_in`
 * are the multi-value ones — on a multiple-choice source they are how you say
 * "show this when they picked A or B or C", and unlike `contains` they match
 * option values EXACTLY (contains would also match option_1 inside option_10). */
export function showIfOperatorsForType(type: string | undefined): string[] {
  switch (type) {
    case 'number':
    case 'scale':
    case 'calculation':
      return ['equals', 'not_equals', 'greater_than', 'less_than', 'in', 'not_in'];
    case 'single_choice':
      return ['equals', 'not_equals', 'in', 'not_in'];
    case 'yes_no':
      return ['equals', 'not_equals'];
    case 'multiple_choice':
      return ['in', 'not_in', 'contains'];
    case 'text':
    case 'textarea':
    case 'email':
    case 'phone':
      return ['equals', 'not_equals', 'contains', 'in', 'not_in'];
    default:
      return ['equals', 'not_equals'];
  }
}

/** Eligibility operators offered for a question type (WCR EligibilityRuleBuilder). */
export function eligibilityOperatorsForType(type: string): string[] {
  switch (type) {
    case 'number':
    case 'scale':
    case 'calculation':
      return ['equals', 'not_equals', 'greater_than', 'less_than', 'between', 'not_between'];
    case 'single_choice':
    case 'yes_no':
      return ['equals', 'not_equals'];
    case 'multiple_choice':
      return ['contains', 'in'];
    case 'text':
    case 'textarea':
    case 'email':
    case 'phone':
      return ['equals', 'not_equals', 'contains'];
    default:
      return ['equals', 'not_equals'];
  }
}

export interface CalcSlot {
  name: string;
  label: string;
  /** Question types accepted as the source for this slot. */
  accept: string[];
}

export interface CalcPresetMeta {
  label: string;
  description: string;
  inputs: CalcSlot[];
}

/** Calculation presets — slots + accepted source types mirror the backend
 * CALC_PRESETS in crmForms.js and the WCR calculations.ts unit conventions
 * (weight in lbs, height as total inches). */
export const CALC_PRESETS: Record<string, CalcPresetMeta> = {
  age: {
    label: 'Age (from date of birth)',
    description: 'Computes age in years from a date-of-birth question.',
    inputs: [{ name: 'dateOfBirth', label: 'Date of birth question', accept: ['date'] }],
  },
  bmi: {
    label: 'BMI (from weight + height)',
    description: 'Computes BMI from a weight (lbs) question and a height question.',
    inputs: [
      { name: 'weightKg', label: 'Weight (lbs) question', accept: ['number'] },
      { name: 'heightCm', label: 'Height question', accept: ['number', 'height'] },
    ],
  },
  // The two-point change family — "% weight change since three months ago" is
  // the driving case. Unit-free: whatever the two questions collect (lbs, kg,
  // inches), the arithmetic is the same.
  percent_decrease: {
    label: '% decrease (e.g. % weight loss)',
    description: 'How far the value fell from the earlier one, as a percent. POSITIVE when it went down — a 220 → 200 lb answer gives 9.1. Use this for "lost at least 5%" criteria.',
    inputs: [
      { name: 'baseline', label: 'Earlier value question (e.g. weight 3 months ago)', accept: ['number', 'scale', 'height'] },
      { name: 'current', label: 'Current value question (e.g. weight now)', accept: ['number', 'scale', 'height'] },
    ],
  },
  percent_change: {
    label: '% change (signed)',
    description: 'Percent change from the earlier value to the current one. NEGATIVE when it went down — a 220 → 200 lb answer gives -9.1.',
    inputs: [
      { name: 'baseline', label: 'Earlier value question', accept: ['number', 'scale', 'height'] },
      { name: 'current', label: 'Current value question', accept: ['number', 'scale', 'height'] },
    ],
  },
  difference: {
    label: 'Difference (current − earlier)',
    description: 'The change in the answers’ own units. NEGATIVE when it went down — a 220 → 200 lb answer gives -20.',
    inputs: [
      { name: 'baseline', label: 'Earlier value question', accept: ['number', 'scale', 'height'] },
      { name: 'current', label: 'Current value question', accept: ['number', 'scale', 'height'] },
    ],
  },
};

export const CALC_PRESET_KEYS = Object.keys(CALC_PRESETS);
